import * as T from 'three';
import {createVolcanoActivity,volcanoActivity,volcanoEjectum,VOLCANO_EJECTA_SLOTS,weatherFromClimate,
 type PlanetSampler,type VolcanoEjectum,type WeatherColumn,type TerrainSample} from '@hyperlight/shared';

let reviewTime:number|null=null;
export function setVolcanicReviewTime(time:number|null):void {reviewTime=time!==null&&Number.isFinite(time)?time:null;}
export function volcanicTime(worldSeconds:number):number{return reviewTime??worldSeconds;}

/** Near-planet owner. The plume is integrated in the existing depth-aware volume
 * pass; only actual molten fragments belong to this scene group. */
export class VolcanicEffects {
 readonly group=new T.Group();
 readonly plumeUniforms={
  uVentCount:{value:0},
  uVentOrigin:{value:Array.from({length:3},()=>new T.Vector3())},
  uVentUp:{value:Array.from({length:3},()=>new T.Vector3())},
  uVentRight:{value:Array.from({length:3},()=>new T.Vector3())},
  uVentForward:{value:Array.from({length:3},()=>new T.Vector3())},
  uVentShape:{value:Array.from({length:3},()=>new T.Vector4())},
  uVentWindPhase:{value:Array.from({length:3},()=>new T.Vector4())},
  uVentNoiseTime:{value:0},
 };
 readonly stats={vents:0,particles:0,stages:[] as string[],time:0};
 readonly audio:{id:string;pos:T.Vector3;level:number}[]=[];
 private readonly geometry=new T.IcosahedronGeometry(1,0);
 private readonly material=new T.MeshStandardMaterial({color:0x4a1607,emissive:0xff4307,emissiveIntensity:1.5,roughness:.92,flatShading:true});
 private readonly mesh:T.InstancedMesh;
 private readonly matrix=new T.Matrix4();
 private readonly point=new T.Vector3();
 private readonly scale=new T.Vector3();
 private readonly velocity=new T.Vector3();
 private readonly quat=new T.Quaternion();
 private readonly axis=new T.Vector3(0,1,0);
 private readonly colour=new T.Color();
 private readonly state=createVolcanoActivity();
 private readonly fragment={} as VolcanoEjectum;
 private readonly weather={} as WeatherColumn;
 private readonly climate:{temp:number;moist:number}[]=[];
 constructor(readonly sampler:PlanetSampler) {
  this.group.name='volcanic-activity';
  this.mesh=new T.InstancedMesh(this.geometry,this.material,sampler.volcanoes().length*VOLCANO_EJECTA_SLOTS);
  this.mesh.name='volcanic:molten-fountain';this.mesh.frustumCulled=false;
  this.mesh.instanceMatrix.setUsage(T.DynamicDrawUsage);this.group.add(this.mesh);
  if(this.mesh.instanceMatrix.count>0)this.mesh.setColorAt(0,new T.Color(1,1,1));
  // Multiplicative per-instance heat gives the fragments darkening edges, while
  // geometry shrinks smoothly at birth/return instead of blinking on a pool.
  this.material.onBeforeCompile=s=>{
   s.fragmentShader=s.fragmentShader.replace('#include <emissivemap_fragment>','#include <emissivemap_fragment>\n#ifdef USE_COLOR\ntotalEmissiveRadiance *= vColor;\n#endif');
  };
  this.material.customProgramCacheKey=()=> 'volcanic-fragments-v1';
  for(const v of sampler.volcanoes()) {
   const s=sampler.sample(...v.origin,{} as TerrainSample,true);this.climate.push({temp:s.temp,moist:s.moist});
   this.audio.push({id:`vent:${sampler.def.seed}:${v.seed}`,pos:new T.Vector3(),level:0});
  }
  this.setTime(0);
 }
 setTime(worldSeconds:number):void {
  const seconds=volcanicTime(worldSeconds),vents=this.sampler.volcanoes(),u=this.plumeUniforms;
  this.stats.vents=vents.length;this.stats.particles=0;this.stats.time=seconds;this.stats.stages.length=0;
  u.uVentCount.value=this.sampler.def.atmosphere?vents.length:0;
  // Periodic vertical texture advection: repeat along a 128-cell density field.
  u.uVentNoiseTime.value=((seconds%640)+640)%640;
  for(let i=0;i<vents.length;i++) {
   const v=vents[i],radius=this.sampler.def.radius+v.poolM,c=this.climate[i];
   volcanoActivity(v,seconds,this.state);this.stats.stages.push(this.state.stage);
   const up=u.uVentUp.value[i].set(...v.origin),right=u.uVentRight.value[i].set(...v.across),forward=u.uVentForward.value[i].set(...v.downhill);
   const origin=u.uVentOrigin.value[i].copy(up).multiplyScalar(radius);
   weatherFromClimate(this.sampler.def,...v.origin,seconds,c.temp,c.moist,this.weather);
   const wind=this.velocity.set(this.weather.windx,this.weather.windy,this.weather.windz).multiplyScalar(this.weather.windSpeed*16);
   const size=Math.max(.22,Math.min(1.5,v.radius/1400));
   u.uVentShape.value[i].set(Math.max(5,v.poolRadius*.23),v.height*1.50+220*size,v.height*.92+150*size,(v.seed>>>0)%97);
   u.uVentWindPhase.value[i].set(wind.dot(right),wind.dot(forward),this.state.phase,this.state.period);
   this.audio[i].level=.025+.34*this.state.warning+.60*this.state.fountain;
   for(let slot=0;slot<VOLCANO_EJECTA_SLOTS;slot++) {
    volcanoEjectum(v,slot,seconds,this.fragment);const f=this.fragment;
    if(!f.visible||f.heat<.001)continue;
    this.point.copy(origin).addScaledVector(right,f.x).addScaledVector(up,f.y).addScaledVector(forward,f.z);
    this.velocity.copy(right).multiplyScalar(f.vx).addScaledVector(up,f.vy).addScaledVector(forward,f.vz).normalize();
    this.quat.setFromUnitVectors(this.axis,this.velocity);
    const s=f.size*Math.min(1,f.heat*6);this.scale.set(s,s*(1.4+f.heat),s);
    this.matrix.compose(this.point,this.quat,this.scale);const index=this.stats.particles++;
    this.mesh.setMatrixAt(index,this.matrix);this.colour.setRGB(.45+.55*f.heat,.16+.84*f.heat,.06+.94*f.heat);this.mesh.setColorAt(index,this.colour);
   }
  }
  this.mesh.count=this.stats.particles;this.mesh.instanceMatrix.needsUpdate=true;
  if(this.mesh.instanceColor)this.mesh.instanceColor.needsUpdate=true;
 }
 audioSources(center:T.Vector3,rotation:T.Quaternion):typeof this.audio {
  for(let i=0;i<this.audio.length;i++)this.audio[i].pos.copy(this.plumeUniforms.uVentOrigin.value[i]).applyQuaternion(rotation).add(center);
  return this.audio;
 }
 dispose():void {this.group.removeFromParent();this.mesh.dispose();this.geometry.dispose();this.material.dispose();}
}
